import type { Skill } from "./types";

export type SkillTag =
  | "core"
  | "products"
  | "categories"
  | "additions"
  | "stock"
  | "schedule"
  | "tables"
  | "orders"
  | "kitchen"
  | "cashier"
  | "cash-session"
  | "cash-register"
  | "delivery"
  | "reservations"
  | "clients"
  | "credit"
  | "members"
  | "invitations"
  | "alerts"
  | "bank-transfers"
  | "restaurant"
  | "preferences"
  | "analytics";

/**
 * Mapa keyword → tags. El primer match añade los tags al set relevante.
 * Pensado para queries en español + algunos términos comunes en inglés.
 */
const KEYWORD_TAG_MAP: Record<string, SkillTag[]> = {
  // ── Catálogo / productos ──
  producto: ["products", "categories", "additions", "stock"],
  menú: ["products", "categories", "preferences"],
  menu: ["products", "categories", "preferences"],
  carta: ["products", "categories", "preferences"],
  categori: ["categories", "products"],
  precio: ["products"],
  stock: ["stock", "products", "alerts"],
  inventario: ["stock", "products"],
  horario: ["schedule", "products"],
  promo: ["schedule", "products"],
  descuent: ["schedule", "products"],
  adicion: ["additions", "products"],
  extra: ["additions", "products"],
  archivar: ["products", "additions", "cash-register"],
  combo: ["products"],
  imagen: ["products", "restaurant"],
  foto: ["products", "restaurant"],

  // ── Mesas y salón ──
  mesa: ["tables", "orders", "reservations"],
  salón: ["tables"],
  zona: ["tables"],
  terraza: ["tables"],

  // ── Pedidos ──
  pedido: ["orders", "kitchen", "cashier"],
  orden: ["orders", "cashier"],
  ordenes: ["orders", "cashier"],
  tomar: ["orders"],
  domicilio: ["orders", "delivery", "clients"],
  delivery: ["orders", "delivery"],
  entrega: ["orders", "delivery"],
  cocina: ["kitchen", "orders"],
  ítem: ["orders"],
  item: ["orders"],
  ronda: ["orders", "cashier"],

  // ── Caja / cobro ──
  caja: ["cashier", "cash-session", "cash-register"],
  cobr: ["cashier", "cash-session", "credit"],
  factur: ["cashier", "orders"],
  pago: ["cashier", "credit"],
  efectivo: ["cashier", "cash-session"],
  apertura: ["cash-session", "cash-register"],
  cierre: ["cash-session", "orders"],
  abrir: ["cash-session"],
  cerrar: ["cash-session", "orders"],
  registradora: ["cash-register"],

  // ── Reservas ──
  reserva: ["reservations", "tables", "clients"],
  calendario: ["reservations"],

  // ── Clientes / crédito ──
  cliente: ["clients", "credit", "reservations"],
  deuda: ["credit", "clients"],
  crédit: ["credit", "clients"],
  credito: ["credit", "clients"],
  abono: ["credit"],
  fiado: ["credit"],

  // ── Personal ──
  personal: ["members", "invitations"],
  empleado: ["members"],
  miembro: ["members"],
  staff: ["members"],
  invit: ["invitations", "members"],
  rol: ["members"],
  permiso: ["members"],
  mesero: ["members"],
  chef: ["members", "kitchen"],
  cajero: ["members", "cashier"],
  admin: ["members"],

  // ── Alertas ──
  alerta: ["alerts", "stock"],
  notif: ["alerts"],
  aviso: ["alerts"],

  // ── Intermediarios ──
  transfer: ["bank-transfers", "cashier"],
  banco: ["bank-transfers"],
  intermediar: ["bank-transfers"],
  nequi: ["bank-transfers"],
  daviplata: ["bank-transfers"],
  bancolombia: ["bank-transfers"],

  // ── Restaurante / preferencias ──
  restaurante: ["restaurant", "preferences"],
  preferencia: ["preferences"],
  config: ["preferences", "restaurant"],
  diseño: ["preferences"],
  paleta: ["preferences"],
  logo: ["restaurant"],
  banner: ["restaurant"],
  dirección: ["restaurant"],
  teléfono: ["restaurant"],

  // ── Analytics ──
  estadística: ["analytics"],
  estadisticas: ["analytics"],
  venta: ["analytics", "cashier"],
  ventas: ["analytics", "cashier"],
  ingreso: ["analytics"],
  reporte: ["analytics"],
  analítica: ["analytics"],
  analitica: ["analytics"],
  performance: ["analytics"],
  kpi: ["analytics"],
  comparat: ["analytics"],
};

/** Skills siempre incluidas (discovery + navegación + guías). */
const ALWAYS_TAGS: SkillTag[] = ["core"];

/**
 * Selecciona el set de skills relevantes basándose en el último mensaje
 * del usuario. Si no hay matches → devuelve TODAS (fallback seguro).
 */
export function filterSkillsByQuery(
  allSkills: Skill[],
  skillTags: Map<string, SkillTag[]>,
  userMessage: string,
): Skill[] {
  const lower = (userMessage || "").toLowerCase();
  const matchedTags = new Set<SkillTag>(ALWAYS_TAGS);
  let anyMatch = false;
  for (const [kw, tags] of Object.entries(KEYWORD_TAG_MAP)) {
    if (lower.includes(kw)) {
      anyMatch = true;
      tags.forEach((t) => matchedTags.add(t));
    }
  }

  // Si el query no matchea con NADA específico, devolver todas las skills
  // (asume que el user está conversando o haciendo algo no clasificable).
  if (!anyMatch) return allSkills;

  return allSkills.filter((s) => {
    const tags = skillTags.get(s.id) ?? [];
    return tags.some((t) => matchedTags.has(t));
  });
}
